import { useRef } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useNavigate } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { formatMoneyValue } from "@utils/Currency";
import styles from "./style.module.scss";
import moment from "moment";
import { CreditnoteDataType } from "@/api/CreditnoteApi";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { renderEDocumentStatusOffCanvasTrigger } from "@utils/ModelIndexUtils";
import { ATV_PENDING_NOTE_MESSAGE, ensureInvoiceAtvAnswered } from "@utils/edocumentAtvGuard";
import { useAppSelector } from "@/store/hooks";

type CreditnoteInvoiceSelectProp = {
  priceNote?: boolean
}
const CreditnoteInvoiceSelect = (props: CreditnoteInvoiceSelectProp) => {
  const { priceNote = false } = props
  const modelRef = useRef<ModelIndexHandle>(null);
  const navigate = useNavigate();
  const suscriptionData = useAppSelector((state) => state.suscription.suscription);
  const einvoiceActive = Boolean(suscriptionData?.einvoice_status);
  const addPath = priceNote ? "/user/creditnotes/addByPrice" : "/user/creditnotes/add";

  const triggerCreditnoteCreation = async (id: string, item: InvoiceDataType) => {
    // Una nota de credito referencia la clave de la factura: mientras Hacienda no
    // resuelva el comprobante original no se puede emitir.
    const canCreate = await ensureInvoiceAtvAnswered(item, einvoiceActive, ATV_PENDING_NOTE_MESSAGE);
    if (!canCreate) {
      return;
    }
    navigate(`${addPath}/${id}`);
  }

  return (
    <PageContainer
      title="Lista de Facturas"
      backUrl="/user/dashboard-accounting"
    >
      <ModelIndex<InvoiceDataType>
        ref={modelRef}
        messages={{
          noResults: "No hay facturas creadas aun"
        }}
        meiliConfig={{
          index: "invoices",
          sort: ["fecha:desc"],
          filter: "status !='anulada'"
        }}
        fetchData={(...args) => InvoiceApi.fetchAll(...args)}
        filterProps={[
          {
            key: "sequence",
            filterType: "contains"
          },
          {
            key: "invoice_key",
            filterType: "contains"
          },
          {
            key: "numero",
            filterType: "eq",
            type: "number"
          }
        ]}
        primaryProperties={[
          {
            key: "sequence",
            highlighted: true,
            className: styles.sequence,
            optionalHighlighted: (val: any, data: CreditnoteDataType) => { return false },
            highlightedColors: {
              "true": {
                "bg": "#dc3545",
                "text": "#fff"
              },
              "false": {
                "bg": "#337ab7",
                "text": "#fff"
              }
            },
          },
          {
            key: (d) => d.nombre_factura || d.client?.nombre || "",
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "4", xs: "12" },
            { sm: "8", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.fecha, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => renderEDocumentStatusOffCanvasTrigger(data, { model: "Invoice", id: `${data.id}` }),
            title: "Estado ATV"
          },
          {
            key: (data) => {
              if ("currency_id" in data) {
                return formatMoneyValue(data.total, (data as any).currency_id)
              }
              return formatMoneyValue(data.total, data.currency.currency_id)
            },
            title: "Total",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            // Sin `href`: un enlace real permitiria abrir la pantalla en otra pestania
            // (ctrl+click) saltandose la validacion del estado ATV.
            onClick: (id, item) => triggerCreditnoteCreation(id, item),
            actionTitle: "Crear Nota de Credito",
            iconProps: { icon: faXmark, className: styles.cancelBtn }
          },
        ]}
      >
      </ModelIndex>
    </PageContainer >
  );
}

export default CreditnoteInvoiceSelect;
